import { IExtendedTelemetryItem, IEventProperty, EventPropertyType, getISOString } from '@ms/1ds-core-js';
import { TelemetryEvent, DataField, DataFieldType, DataClassification } from '@ms/oteljs';

// Constants
const DATA_TOKEN = 'Data.';
const CONTRACT_TOKEN = 'zC.';

/**
 * Aria token for Office.Telemetry
 */
export const ARIA_OFFICE_TELEMETRY_TOKEN = 'f998cc5ba4d448d6a1e8e913ff18be94-dd122e0a-fcf8-4dc5-9dbb-6afac5325183-7405'; // Office.Telemetry

export interface IEventData {
  telemetryEvent: TelemetryEvent;
  oneDSSinkSessionId: string;
  eventSequence: number;
  timestamp?: number;
}

/**
 * Create a 1DS telemetry event with the Event contract and data fields
 * @param telemetryEvent - The telemetry event to be sent
 * @param oneDSSinkSessionId - A sessionId for the OneDSSink to be used as part of the Event.Id for deduping purposes.
 * WAC sessionIds may show up in multiple sessions.
 * @param timestamp - Time the event was sent
 */
export function getOneDSData(eventData: IEventData): IExtendedTelemetryItem['data'] {
  let oneDSEvent: IExtendedTelemetryItem['data'] = {};
  addOneDSFields(oneDSEvent, eventData.telemetryEvent.dataFields, true);
  oneDSEvent['Event.Sequence'] = { value: eventData.eventSequence, propertyType: EventPropertyType.Int64 };
  oneDSEvent['Event.Name'] = eventData.telemetryEvent.eventName;
  oneDSEvent['Event.Source'] = 'OTelJS';
  oneDSEvent['Event.Time'] = { value: getISOString(getTimestamp(eventData.timestamp)), propertyType: EventPropertyType.DateTime };

  if (eventData.oneDSSinkSessionId) {
    oneDSEvent['Event.Id'] = eventData.oneDSSinkSessionId + '.' + eventData.eventSequence;
  }

  // Event Contract
  if (eventData.telemetryEvent.eventContract) {
    oneDSEvent['Event.Contract'] = eventData.telemetryEvent.eventContract.name;
    addOneDSFields(oneDSEvent, eventData.telemetryEvent.eventContract.dataFields, false);
  }

  return oneDSEvent;
}

/**
 * Get a properly formatted timestamp
 * @param timestamp - telemetry event timestamp
 */
function getTimestamp(timestamp?: number): Date {
  let timestampLocal: Date;
  if (timestamp) {
    timestampLocal = new Date(timestamp);
  } else {
    timestampLocal = new Date();
  }

  return timestampLocal;
}

/**
 * Add OTelJS DataField fields to a 1DS data object
 * @param data The object to hold 1DS fields
 * @param fields The DataField fields to add
 * @param prependDataToken Boolean, whether or not to prepend "Data."
 */
export function addOneDSFields(data: IExtendedTelemetryItem['data'], fields: DataField[] | undefined, prependDataToken: boolean): void {
  if (fields) {
    fields.forEach(field => {
      // We can only send SystemMetadata
      if (
        field.classification &&
        !(
          field.classification === DataClassification.SystemMetadata || field.classification === DataClassification.EssentialServiceMetadata
        )
      ) {
        return;
      }

      let modifiedFieldName;
      if (prependDataToken) {
        if (field.name.substr(0, 3) === CONTRACT_TOKEN) {
          modifiedFieldName = CONTRACT_TOKEN + DATA_TOKEN + field.name.substr(3);
        } else {
          modifiedFieldName = DATA_TOKEN + field.name;
        }
      } else {
        modifiedFieldName = field.name;
      }

      let eventPropertyType: number;
      switch (field.dataType) {
        case DataFieldType.Double:
          eventPropertyType = EventPropertyType.Double;
          break;
        case DataFieldType.Int64:
          eventPropertyType = EventPropertyType.Int64;
          break;
        case DataFieldType.Guid:
          eventPropertyType = EventPropertyType.Guid;
          break;
        case DataFieldType.String:
          // Strings and booleans are set directly, not as IEventProperties -
          // tables get created with the appropriate data types on the collector side.
          // This helps to save about 25% of the event size!
          // The 1DS SDK assigns a default type of Double to any numbers, so we still
          // to send the data types for those values.

          // Limit the maximum allowed string length on a datafield to avoid accidentally going over
          // the maximum allowed serialized size for a 1DS event and having the event dropped.
          // That and 1ds-post v2.4.4 and v3.0.0 have a bug that will put the code into an infinite
          // loop if that condition is trigged.
          data![modifiedFieldName] = typeof field.value === 'string' ? field.value.substr(0, 25000) : field.value;
          return;
        case DataFieldType.Boolean:
        default:
          data![modifiedFieldName] = field.value;
          return;
      }

      let eventProperty: IEventProperty = {
        value: field.value,
        propertyType: eventPropertyType
      };

      data![modifiedFieldName] = eventProperty;
    });
  }
}
